import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { appColors } from '../../Config/Theme';
import FiltersButton from '../Elements/FiltersButton';
import { SortMode, SORT_OPTIONS } from '../../Utils/headcountFilters';
import type { AlphaRange } from '../../Utils/alphaRangeHelpers';

/** Quick-filter alpha ranges shown directly in the toolbar. */
const TOOLBAR_ALPHA_RANGES: Exclude<AlphaRange, 'All'>[] = ['A-F', 'G-L', 'M-R', 'S-Z'];

const ALPHA_ARIA_LABELS: Record<string, string> = {
  'A-F': 'Filter roster to last names A through F',
  'G-L': 'Filter roster to last names G through L',
  'M-R': 'Filter roster to last names M through R',
  'S-Z': 'Filter roster to last names S through Z',
};

/** Shared selected/idle styling for the alpha-group buttons (All + ranges). */
const alphaButtonBaseSx = (isActive: boolean) => ({
  minHeight: 44,
  borderRadius: '8px',
  fontWeight: 700,
  textTransform: 'none' as const,
  whiteSpace: 'nowrap' as const,
  borderColor: isActive ? appColors.primary.main : appColors.border.default,
  bgcolor: isActive ? `${appColors.primary.main}14` : appColors.surface.card,
  color: isActive ? appColors.primary.main : appColors.text.body,
  boxShadow: isActive ? `inset 0 0 0 1px ${appColors.primary.main}` : 'none',
  '&:hover': {
    borderColor: appColors.primary.main,
    bgcolor: isActive ? `${appColors.primary.main}1F` : `${appColors.primary.main}08`,
  },
  '&:focus-visible': {
    outline: `2px solid ${appColors.primary.main}`,
    outlineOffset: 2,
  },
});

interface HeadcountToolbarProps {
  searchText: string;
  onSearchChange: (value: string) => void;
  sortMode: SortMode;
  onSortChange: (value: SortMode) => void;
  activeFilterCount: number;
  onFiltersClick: () => void;
  alphaRange: AlphaRange;
  onAlphaRangeChange: (value: AlphaRange) => void;
}

const HeadcountToolbar: React.FC<HeadcountToolbarProps> = ({
  searchText,
  onSearchChange,
  sortMode,
  onSortChange,
  activeFilterCount,
  onFiltersClick,
  alphaRange,
  onAlphaRangeChange,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: { xs: 1, sm: 2 },
      flexWrap: 'wrap',
    }}
  >
    {/* Search */}
    <TextField
      size="small"
      placeholder="Search by name, phone, department..."
      value={searchText}
      onChange={(e) => onSearchChange(e.target.value)}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 20, color: appColors.text.placeholder }} />
            </InputAdornment>
          ),
        },
      }}
      sx={{
        flex: '1 1 200px',
        minWidth: { xs: '100%', sm: 200 },
        maxWidth: 420,
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          bgcolor: appColors.surface.card,
          minHeight: 44,
        },
      }}
    />

    {/* Sort */}
    <FormControl size="small" sx={{ minWidth: { xs: 140, sm: 170 } }}>
      <Select
        value={sortMode}
        onChange={(e) => onSortChange(e.target.value as SortMode)}
        sx={{
          borderRadius: '8px',
          bgcolor: appColors.surface.card,
          fontSize: '0.85rem',
          minHeight: 44,
        }}
      >
        {SORT_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            Sort: {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>

    {/* Alpha range quick-filters */}
    <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
    {/* All — explicit unfiltered state; selected when no alpha group is active */}
    <Button
      variant="outlined"
      aria-pressed={alphaRange === 'All'}
      aria-label="Show all alphabetical groups"
      onClick={() => onAlphaRangeChange('All')}
      sx={{
        ...alphaButtonBaseSx(alphaRange === 'All'),
        minWidth: { xs: 44, sm: 76 },
        px: { xs: 1.25, sm: 2 },
        fontSize: { xs: '1rem', sm: '1.15rem' },
      }}
    >
      All
    </Button>
    {TOOLBAR_ALPHA_RANGES.map((range) => {
      const isActive = alphaRange === range;
      return (
        <Button
          key={range}
          variant="outlined"
          aria-pressed={isActive}
          aria-label={ALPHA_ARIA_LABELS[range]}
          onClick={() => onAlphaRangeChange(isActive ? 'All' : range)}
          sx={{
            ...alphaButtonBaseSx(isActive),
            minWidth: 44,
            px: { xs: 1.5, sm: 3 },
            fontSize: { xs: '1.3rem', sm: '1.7rem' },
          }}
        >
          {range}
        </Button>
      );
    })}
    </Box>

    {/* Filters button */}
    <FiltersButton onClick={onFiltersClick} activeFilterCount={activeFilterCount} />
  </Box>
);

export default React.memo(HeadcountToolbar);
